import { useEffect, useState } from 'react';

/**
 * Minimal module-level pub/sub toast system. `showToast` works from anywhere
 * (including non-React code like the apiClient 403 interceptor); `<Toaster />`
 * is mounted once in AppShell and renders the stack bottom-right.
 */
const subscribers = new Set();
let nextId = 0;

const TOAST_TTL_MS = 5000;

/**
 * Dieselbe Meldung nicht mehrfach übereinander stapeln.
 *
 * Eine Seite holt oft mehrere Dinge gleichzeitig. Scheitern sie am selben
 * Grund - kein Zugriff, keine Personalakte -, antwortet der Abfangjäger auf
 * JEDE Anfrage einzeln, und der gleiche Satz stand dreimal untereinander.
 * Auf dem Bildschirm sieht das aus, als seien drei verschiedene Dinge
 * schiefgegangen.
 *
 * Zwei Sekunden Gedächtnis: lange genug für die Anfragen eines Seitenaufbaus,
 * kurz genug, dass eine zweite Meldung nach einer erneuten Handlung wieder
 * durchkommt - sonst wäre stilles Verschlucken der nächste Fehler.
 */
const GEDAECHTNIS_MS = 2000;
const zuletzt = new Map();

export function showToast(message, variant = 'danger') {
  const schluessel = `${variant}:${message}`;
  const jetzt = Date.now();
  const vorhin = zuletzt.get(schluessel);
  if (vorhin && jetzt - vorhin < GEDAECHTNIS_MS) {
    return;
  }
  zuletzt.set(schluessel, jetzt);
  /* Aufräumen, damit die Karte nicht mit jeder Meldung wächst. */
  for (const [k, t] of zuletzt) {
    if (jetzt - t >= GEDAECHTNIS_MS) zuletzt.delete(k);
  }

  const toast = { id: ++nextId, message, variant };
  subscribers.forEach((notify) => notify(toast));
}

export function Toaster() {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    const timers = new Map();
    function handleToast(toast) {
      setToasts((current) => [...current, toast]);
      const timer = setTimeout(() => {
        timers.delete(toast.id);
        setToasts((current) => current.filter((t) => t.id !== toast.id));
      }, TOAST_TTL_MS);
      timers.set(toast.id, timer);
    }
    subscribers.add(handleToast);
    return () => {
      subscribers.delete(handleToast);
      timers.forEach((timer) => clearTimeout(timer));
    };
  }, []);

  function dismiss(id) {
    setToasts((current) => current.filter((t) => t.id !== id));
  }

  return (
    <div className="toaster" role="status" aria-live="polite">
      {toasts.map((toast) => (
        <button
          key={toast.id}
          type="button"
          className={`toast toast-${toast.variant}`}
          onClick={() => dismiss(toast.id)}
        >
          {toast.message}
        </button>
      ))}
    </div>
  );
}
